/**
 * i cba be to figure out the path so leave it here unless you want to fic ti
 */
import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CharcoalSketch from './charcoal-sketch.png';  // Adjust path as needed
import StudioAnimation from './studio-animation.png';  // Adjust path as needed
import DisneyMagic from './disney-magic.png';
import ExpressiveAbstract from './expressive-abstract.png';
import WaterColorWash from './watercolor-wash.png';
import LegoBuster from './legobuster.png';
import GhibliDreamscape from './ghibli-dreamscape.png';
import StoryBookIllustration from './storybook-illustration.png';
import AnimeClassic from './anime-classic.png';
import MineCraft from './minecraft.png';
import Photorealism from './photo-realism.png';
import VividComicBook from './vivid-comic-book.png';
import PopArtComic from './pop-art-comic.png'
import CyberPunkNeon from './cyberpunk-neon.png'
import RetroPixelArt from './retro-pixel-art.png'
import GTAGaming from './gta-gaming.png'



const ArtStyleSelector = ({ selectedStyle, onSelectStyle }) => {
    const carouselRef = useRef(null);
    const artStyles = [
        // Mainstream/Popular Styles
        {
            id: 'photorealistic',
            name: 'Photorealistic',
            description: 'Stunning true-to-life imagery with perfect details',
            imageUrl: Photorealism,
            prompt: "A mountain lake at dawn with perfect reflections in still water, volumetric fog rising from the surface, pine trees with individually visible needles, rocks with detailed moss and lichen textures, and atmospheric lighting creating god rays through morning mist",
            params: 'Ultra-detailed imagery with perfect lighting, textures, and shadows that precisely mimic real-world photography. Features natural color grading, accurate depth of field, and photographic imperfections like subtle lens flare and grain.'
        },
        {
            id: 'vivid-comic-book',
            name: 'Vivid Comic Book',
            description: 'Bold, dynamic illustrations with exaggerated action and color',
            imageUrl: VividComicBook,
            prompt: "A dramatic scene of two rivals facing off in a rainy alley, with bold black outlines, exaggerated proportions, dynamic action poses, speed lines indicating movement, saturated primary colors, halftone dot shading patterns, dramatic perspective angles, and classic speech bubbles with expressive typography",
            params: 'Eye-catching illustrated style featuring thick outlines, flat vibrant colors, dramatic shadows, exaggerated anatomy, and dynamic composition. Includes comic-specific elements like action lines, impact effects, expressive typography, and stylized emotional expressions.'
        },
        {
            id: 'studio-animation',
            name: 'Studio Animation',
            description: 'Polished 3D animation with smooth movement and rich detail',
            imageUrl: StudioAnimation,
            prompt: "A family picnic in a sunlit meadow with stylized characters featuring slightly exaggerated facial features, subsurface skin scattering, hair with individual strand movement, cloth simulation on flowing garments, depth of field focusing on emotional expressions, and ambient occlusion enhancing dimensional space",
            params: 'High-end 3D animation featuring smooth movement, sophisticated lighting effects, subtly exaggerated character proportions, and meticulously crafted environments with industry-standard texturing and rendering techniques.'
        },
        {
            id: 'disney-magic',
            name: 'Disney Magic',
            description: 'Enchanting animation with expressive characters and vibrant worlds',
            imageUrl: DisneyMagic,
            prompt: "A young explorer with large expressive eyes discovering a magical forest glade where flowers glow softly, friendly animal companions with exaggerated facial expressions gather around, golden sunlight filters through canopy creating visible light beams, and background elements feature subtle atmospheric perspective with dreamy edge softening",
            params: 'Enchanting animation characterized by fluid movement, expressive characters with large eyes and accentuated features, rich color palettes with magical lighting effects, and whimsical environments that balance realism with fantasy elements.'
        },
        {
            id: 'anime-classic',
            name: 'Anime Classic',
            description: 'Traditional Japanese animation with distinctive character features',
            imageUrl: AnimeClassic,
            prompt: "A determined warrior with flowing, physics-defying hair and oversized expressive eyes facing an approaching storm, dynamic speed lines emphasizing movement, dramatic lighting creating stark shadows across determined facial features, exaggerated weapon proportions with gleaming highlights, and simplified background elements increasing in detail near focal points",
            params: 'Distinctive Japanese animation featuring characters with large, expressive eyes, simplified facial features, dramatic action lines, stylized hair in vibrant colors, and backgrounds that combine detailed elements with simplified areas to direct focus.'
        },

        // Stylized Computer-Generated
        {
            id: 'minecraft-voxel',
            name: 'Minecraft Voxel',
            description: 'Blocky pixel-based worlds with distinctive cubic aesthetic',
            imageUrl: MineCraft,
            prompt: "A medieval village built entirely from uniform cubes with pixelated wooden textures, blocky architecture featuring right-angle construction, waterfall rendered as stacked transparent blue squares, cubic farm animals with single-pixel eyes, blocky trees with geometric leaf clusters, and simplified lighting creating sharp-edged shadows",
            params: 'Distinctive cubic aesthetic where everything is composed of perfectly aligned blocks with limited texture resolution. Features characteristic pixelated water effects, rigid geometric forms, and a uniquely recognizable lighting system with limited shadow gradation.'
        },
        {
            id: 'lego-blockbuster',
            name: 'LEGO Blockbuster',
            description: 'Colorful brick-built animation with playful plastic charm',
            imageUrl: LegoBuster,
            prompt: "A superhero standing triumphantly atop a skyscraper at sunset, entire scene constructed from visible LEGO bricks with characteristic studs and connection points, figure featuring segmented limbs with limited rotation, plastic material sheen with subtle fingerprint smudges, brick-built clouds with visible construction elements, and city elements showing creative brick solutions for curved surfaces",
            params: 'Hyper-colorful animation featuring characters and environments constructed entirely from LEGO bricks and pieces. Includes characteristic stud patterns, plastic material sheen, limited joint articulation, and playful interpretations of real-world elements.'
        },
        {
            id: 'gta-gaming',
            name: 'GTA Gaming',
            description: 'Stylized urban realism with cinematic lighting and atmosphere',
            imageUrl: GTAGaming,
            prompt: "Two urban characters with slightly exaggerated proportions walking up a grimy city street at night, oversaturated neon signs with bloom effects reflecting in oil-slicked puddles, excessively detailed vehicle surfaces showing precise reflections, cinematic color grading emphasizing teal and orange contrasts, film grain overlay, and dramatic shadows with aggressive rim lighting highlighting character silhouettes",
            params: 'Highly stylized urban realism featuring slightly exaggerated character proportions, dramatic lighting with distinctive color grading, hyper-detailed vehicle rendering, and environments that balance photographic elements with artistic enhancement for maximum visual impact.'
        },

        // Artistic/Illustrated Styles
        {
            id: 'ghibli-dreamscape',
            name: 'Ghibli Dreamscape',
            description: 'Breathtaking hand-drawn animation with dreamlike natural beauty',
            imageUrl: GhibliDreamscape,
            prompt: "A child with softly animated hair running through tall wind-rippled grass towards a mysterious floating castle, clouds with impossible cotton-like volume and structure, meticulously detailed wildflowers and insects in foreground elements, warm afternoon sunlight creating golden rim lighting, background mountains with perfect atmospheric perspective, and tiny background details rewarding careful observation",
            params: 'Breathtaking hand-crafted animation blending hyper-detailed natural elements with fantastical designs. Features masterful use of color and light, meticulous background paintings, characters with subtle expressions, and dreamlike environments that inspire wonder.'
        },
        {
            id: 'storybook-illustration',
            name: 'Storybook Illustration',
            description: 'Charming hand-drawn art perfect for children\'s stories',
            imageUrl: StoryBookIllustration,
            prompt: "A bunny and fox sharing afternoon tea in a cozy tree hollow home with visible brush strokes defining fur textures, slightly anthropomorphic expressions conveying gentle friendship, hand-painted background elements with paper texture showing through color washes, delicate line work defining foreground details, whimsical furniture crafted from natural elements, and soft vignette framing the composition like a page from a beloved children's book",
            params: 'Heartwarming hand-drawn style reminiscent of classic children\'s literature. Features gentle linework, soft watercolor-like coloring with visible texture, charming character designs with slightly anthropomorphic qualities, and inviting environments rich with whimsical details.'
        },
        // Artistic Techniques
        {
            id: 'watercolor-wash',
            name: 'Watercolor Wash',
            description: 'Delicate flowing colors with artistic transparency effects',
            imageUrl: WaterColorWash,
            prompt: "A coastal lighthouse during a storm with pigments bleeding naturally at color boundaries, translucent paint layers revealing underlying washes, visible paper texture influencing pigment distribution, controlled blooms where colors diffuse into wet areas, granulation effects in storm clouds, strategic negative space preserving white paper for highlights, and soft edges created by varying paper wetness",
            params: 'Delicate artistic style featuring translucent color layers with visible bleeding edges, granulation effects in pigment, strategic use of negative space, and the characteristic interaction of pigment with paper texture creating organic, flowing imagery.'
        },
        {
            id: 'charcoal-sketch',
            name: 'Charcoal Sketch',
            description: 'Bold monochromatic artwork with expressive strokes and contrast',
            imageUrl: CharcoalSketch,
            prompt: "A lone twisted tree on a windswept hillside rendered entirely in charcoal with visible smudging creating atmospheric distance, varied stroke pressure showing branch texture, finger-blended shadows with characteristic streaking, strategic eraser marks creating highlight areas, powdery texture in shadow areas, deliberate paper grain showing through lighter areas, and dramatic tonal contrast between deep blacks and untouched paper highlights",
            params: 'Dramatic monochromatic artwork characterized by bold, gestural strokes with varying pressure, strategic smudging for tonal gradation, high contrast between deep blacks and bright highlights, and the distinctive texture of charcoal interacting with paper grain.'
        },
        {
            id: 'expressive-abstract',
            name: 'Expressive Abstract',
            description: 'Bold emotional art using color and form rather than representation',
            imageUrl: ExpressiveAbstract,
            prompt: "A figure dancing expressed through bold geometric color blocks with visible brushwork showing emotional energy, dynamic diagonal compositional lines creating movement, deliberately jarring color relationships evoking emotional intensity, varied paint application techniques from impasto to scraping, textural elements suggesting rather than depicting human form, and strategic negative space creating rhythmic visual pauses",
            params: 'Bold, emotionally charged non-representational artwork featuring dynamic compositional elements, unconventional color relationships, textural variety through different application techniques, and forms that suggest rather than depict their subjects.'
        },
        // Additional Styles
        {
            id: 'cyberpunk-neon',
            name: 'Cyberpunk Neon',
            description: 'Futuristic dystopian cityscape with vibrant neon lighting',
            imageUrl: CyberPunkNeon,
            prompt: "A street-level view of a futuristic megacity at night during heavy rain, neon advertisements reflecting in puddles with digital glitch effects, cybernetically enhanced characters with glowing implants, volumetric fog illuminated by multiple colored light sources, holographic displays with visible projection artifacts, weathered and decaying infrastructure contrasting with advanced technology, and aggressive lens flares from multiple light sources",
            params: 'Futuristic dystopian aesthetic featuring oversaturated neon lighting against dark urban environments, advanced technology with holographic interfaces, dramatic rain effects with reflective surfaces, and a gritty, lived-in world balancing high-tech and decay.'
        },
        {
            id: 'retro-pixel-art',
            name: 'Retro Pixel Art',
            description: 'Nostalgic digital style with limited pixels and color palette',
            imageUrl: RetroPixelArt,
            prompt: "A heroic character in a dungeon environment created with visible pixels at 32x32 resolution, limited 16-color palette with deliberate color choices, hand-crafted dithering creating gradient effects, carefully anti-aliased edges on diagonal lines, chunky block shadows without transparency, sprite-like character design with exaggerated proportions for readability, and background elements constructed from tiled pattern pieces",
            params: 'Nostalgic digital art style with deliberate pixel constraints, limited color palettes reminiscent of early gaming systems, hand-placed pixels creating distinctive dithering patterns, and carefully considered compositions working within technical limitations.'
        },
        {
            id: 'pop-art-comic',
            name: 'Pop Art Comic',
            description: 'Bold graphic style with flat colors and halftone patterns',
            imageUrl: PopArtComic,
            prompt: "A dramatic portrait in pop art style with heavy black outlines, flat unblended CMYK color fields, visible Ben-Day dot patterns creating tonal variations, speech bubbles with bold typography, intentionally offset color registration, highly simplified facial features emphasizing emotional expression, strategic use of primary colors creating maximum visual impact, and composition referencing consumer advertising aesthetics",
            params: 'Bold, graphic style inspired by mid-century commercial art featuring flat color areas, heavy black outlines, halftone dot patterns for shading, primary color palette with high contrast, and stylized simplification of complex forms.'
        }
    ];

    const scroll = (direction) => {
        if (carouselRef.current) {
            const { current } = carouselRef;
            const scrollAmount = direction === 'left' ? -400 : 400;
            current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    // Helper function to get the style value that matches what's stored
    const getStyleValue = (style) => `${style.name} - ${style.params}`;

    return (
        <div>
            <h5 className="text-lg font-medium mb-3 text-gray-700">
                Art Style
            </h5>

            <div className="relative">
                {/* Left scroll button */}
                <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 p-2 rounded-full shadow-md hover:bg-opacity-100"
                    aria-label="Scroll left"
                >
                    <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>

                {/* Carousel container */}
                <div
                    ref={carouselRef}
                    className="flex overflow-x-auto pb-4 pt-1 px-2 -mx-2 scroll-smooth art-style-scrollbar-hide"
                    style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}
                >
                    {artStyles.map((style) => {
                        const styleValue = getStyleValue(style);
                        const isSelected = selectedStyle === styleValue;

                        return (
                            <div
                                key={style.id}
                                className="flex-none mx-2 w-72">
                                <button
                                    type="button"
                                    onClick={() => onSelectStyle(styleValue)}
                                    className={`
                                        w-full flex flex-col items-center p-3 rounded-lg border
                                        ${isSelected
                                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300'
                                        : 'border-gray-200 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-200'
                                    } 
                                        transition-colors duration-300
                                    `}
                                >
                                    <div className="w-full aspect-video mb-3 bg-gray-200">
                                        <img
                                            src={style.imageUrl}
                                            alt={`${style.name} style example`}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <span className="font-medium text-gray-800">{style.name}</span>
                                    <p className="text-xs text-gray-500 mt-1 text-center line-clamp-2">{style.description}</p>
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Right scroll button */}
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 p-2 rounded-full shadow-md hover:bg-opacity-100"
                    aria-label="Scroll right"
                >
                    <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
            </div>
        </div>
    );
};

export default ArtStyleSelector;
